"use client";

import React from "react";
import {
    Box,
    Button,
    Chip,
    Divider,
    Dropdown,
    Menu,
    MenuButton,
    MenuItem,
    ListItemDecorator,
    Skeleton,
    Typography,
} from "@mui/joy";
import { EllipsisVertical, FileDown } from "lucide-react";
import {
    FilePlus,
    Edit3,
    FileCheck2,
    Send,
    Eye,
    PencilLine,
    XCircle,
    Clock,
    Ban,
    Archive,
    AlertTriangle,
} from "lucide-react";

import { IconButton } from "../../shared";
import { useAsset } from "../../hooks/query";

export interface IMenuAction {
    label: string;
    icon?: React.ReactNode;
    onClick: () => void;
    disabled?: boolean;
    color?: "primary" | "neutral" | "danger" | "success" | "warning";
}

interface IDocumentPreviewWidget {
    title: string;
    description: string;
    onDownloadClick?: () => any;
    onButtonClick?: () => any; // Override for download button click behavior
    onPrintClick?: () => any;
    onPreviewClick?: () => any;
    fileType?: string;
    thumbnailUrl?: string;
    status?: any;
    assetId?: string;
    buttonText?: string; // Custom button text (default: "Download")
    buttonIcon?: React.ReactNode; // Custom button icon (default: FileDown icon)
    buttonDisabled?: boolean; // Override disabled state
    menuActions?: IMenuAction[]; // Custom menu actions for the 3-dots menu
}

const fallbackDocThumbnail =
    "https://images.sampletemplates.com/wp-content/uploads/2016/12/21102940/Formal-Business-Document.jpg";

// statusConfig.ts or at top of your component file

export const statusConfig: Record<
    string,
    {
        label: string;
        icon: React.ReactNode;
        color: "primary" | "warning" | "success" | "danger" | "neutral";
    }
> = {
    INIT: { label: "Init", icon: <FilePlus size={14} />, color: "neutral" },
    DRAFT: { label: "Draft", icon: <Edit3 size={14} />, color: "neutral" },
    GENERATED: { label: "Generated", icon: <FileCheck2 size={14} />, color: "primary" },
    SENT: { label: "Sent", icon: <Send size={14} />, color: "primary" },
    VIEWED: { label: "Viewed", icon: <Eye size={14} />, color: "success" },
    SIGNED: { label: "Signed", icon: <PencilLine size={14} />, color: "success" },
    REJECTED: { label: "Rejected", icon: <XCircle size={14} />, color: "danger" },
    EXPIRED: { label: "Expired", icon: <Clock size={14} />, color: "warning" },
    CANCELLED: { label: "Cancelled", icon: <Ban size={14} />, color: "danger" },
    ARCHIVED: { label: "Archived", icon: <Archive size={14} />, color: "neutral" },
    ERROR: { label: "Error", icon: <AlertTriangle size={14} />, color: "danger" },
    "NOT GENERATED": { label: "Error", icon: <AlertTriangle size={14} />, color: "danger" },
};

// export default function DocumentPreviewWidget(props: IDocumentPreviewWidget) {
//     const {
//         title,
//         description,
//         fileType,
//         thumbnailUrl,
//         onDownloadClick,
//         status = "NOT GENERATED",
//         assetId,
//     } = { ...props };

//     const { useDownload } = useAsset();
//     const downloadAssetSvc = useDownload();

//     const downloadDisabled = !onDownloadClick && !assetId;

//     const handleOnDownloadClick = async () => {
//         if (onDownloadClick) {
//             onDownloadClick();
//             return;
//         }

//         const params = {
//             AssetId: assetId,
//         };
//         await downloadAssetSvc.mutateAsync(params);
//     };

//     return (
//         <Box
//             sx={{
//                 border: "1px solid #ddd",
//                 borderRadius: "lg",
//                 minWidth: 600,
//                 maxWidth: 600,
//                 flex: 1,
//                 display: "flex",
//                 px: 2,
//                 py: 1.5,
//                 gap: 2,
//             }}
//         >
//             <Box sx={{ flexBasis: "60%" }}>
//                 <Typography level="body-sm" sx={{ fontWeight: 600 }}>
//                     {title}
//                 </Typography>
//                 <Typography level="body-xs" gutterBottom>
//                     {description}
//                 </Typography>

//                 <Button
//                     startDecorator={<FileDown size={16} />}
//                     size="sm"
//                     sx={{ flex: 1, mt: 1 }}
//                     loading={downloadAssetSvc.isPending}
//                     loadingPosition={"start"}
//                     disabled={downloadDisabled}
//                     onClick={handleOnDownloadClick}
//                 >
//                     Download
//                 </Button>
//             </Box>
//             <Box sx={{ flexBasis: "40%", position: "relative" }}>
//                 <Box
//                     sx={{
//                         p: 1,
//                         border: "1px solid #ddd",
//                         borderRadius: "lg",
//                         position: "absolute",
//                         top: -24,
//                         left: 0,
//                         right: 0,
//                         bottom: -12,
//                         bgcolor: "background.surface",
//                         borderBottom: "none",
//                         borderBottomLeftRadius: 0,
//                         borderBottomRightRadius: 0,
//                     }}
//                 >
//                     Preview
//                 </Box>
//             </Box>
//         </Box>
//     );
// }
export default function DocumentPreviewWidget(props: IDocumentPreviewWidget) {
    const {
        title,
        description,
        fileType,
        thumbnailUrl,
        onDownloadClick,
        onButtonClick,
        status = "NOT GENERATED",
        assetId,
        buttonText = "Download",
        buttonIcon,
        buttonDisabled,
        menuActions = [],
    } = { ...props };

    const { useDownload } = useAsset();
    const downloadAssetSvc = useDownload();

    // Determine disabled state: use buttonDisabled if provided, otherwise calculate
    const downloadDisabled =
        buttonDisabled !== undefined ? buttonDisabled : !onDownloadClick && !onButtonClick && !assetId;

    const handleOnDownloadClick = async () => {
        // Priority: onButtonClick > onDownloadClick > default download
        if (onButtonClick) {
            onButtonClick();
            return;
        }

        if (onDownloadClick) {
            onDownloadClick();
            return;
        }

        if (!assetId) {
            return;
        }

        const params = {
            AssetId: assetId,
        };
        await downloadAssetSvc.mutateAsync(params);
    };

    // Determine which loading state to use (only if using default download)
    const isLoading = !onButtonClick && !onDownloadClick && downloadAssetSvc.isPending;

    return (
        <Box
            sx={{
                width: 230,
                height: 280,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                position: "relative",
            }}
        >
            {/* Background Layer */}
            <Box
                sx={{
                    width: 200,
                    height: 250,
                    //bgcolor: "green",
                    border: "1px solid #ddd",
                    borderRadius: "lg",
                    position: "absolute",
                    top: 0,
                    left: 0,
                    zIndex: 0,
                    background:
                        fileType === "PDF"
                            ? "linear-gradient(169deg, #f8d8d8 -60%, transparent 70%)"
                            : "linear-gradient(169deg, #e1f8d8 -60%, transparent 70%)",
                    transform: "rotate(-2deg)",
                }}
            >
                <Box display={"flex"} flexDirection={"column"} gap={1} p={1}>
                    <Line width={"50%"} />
                    <Line />
                    <Line />
                    <Line />
                    <Line bgcolor={"success.solidBg"} />
                    <Line />
                    <Line />
                    <Line />
                    <Line />
                    <Line />
                    <Line />
                    <Line />
                    <Line />
                    <Line />
                </Box>
            </Box>

            {/* Top Layer with Fold */}
            <Box
                sx={(theme: any) => {
                    return {
                        width: 200,
                        height: 250,
                        bgcolor: theme.palette.background.surface,
                        border: "1px solid #ddd",
                        borderRadius: "lg",
                        zIndex: 1,
                        position: "relative",
                        //boxShadow: "lg",
                        overflow: "hidden",
                        backgroundSize: "cover",
                        backgroundImage: `url(${thumbnailUrl ? thumbnailUrl : fallbackDocThumbnail})`,
                    };
                }}
            >
                <DocumentStatusChip status={status} />
                <Box
                    sx={(theme: any) => {
                        return {
                            position: "absolute",
                            bottom: 0,
                            left: 0,
                            right: 0,
                            //top: "66%",
                            p: 1,
                            pl: 1.5,
                            backdropFilter: "blur(30px)",
                            background: `linear-gradient(${theme.palette.background.surface} 10%, transparent)`,
                        };
                    }}
                >
                    <Divider inset="context" sx={{ mb: 1 }} />
                    <Typography level="body-sm" sx={{ fontWeight: 600 }}>
                        {title}
                    </Typography>
                    <Typography level="body-xs" gutterBottom>
                        {description}
                    </Typography>
                    <Divider inset="context" sx={{ mb: 1 }} />
                    <Box display={"flex"} gap={1} justifyContent={"space-between"}>
                        <Button
                            startDecorator={buttonIcon || <FileDown size={16} />}
                            size="sm"
                            sx={{ flex: 1 }}
                            loading={isLoading}
                            loadingPosition={"start"}
                            disabled={downloadDisabled}
                            onClick={handleOnDownloadClick}
                        >
                            {buttonText}
                        </Button>
                        {menuActions.length > 0 ? (
                            <Dropdown>
                                <MenuButton
                                    slots={{ root: IconButton }}
                                    slotProps={{ root: { size: "sm", variant: "outlined", color: "neutral" } }}
                                >
                                    <EllipsisVertical size={16} />
                                </MenuButton>
                                <Menu placement="bottom-end" sx={{ zIndex: 10001 }}>
                                    {menuActions.map((action, index) => (
                                        <MenuItem
                                            key={index}
                                            onClick={action.onClick}
                                            disabled={action.disabled}
                                            color={action.color}
                                        >
                                            {action.icon && <ListItemDecorator>{action.icon}</ListItemDecorator>}
                                            {action.label}
                                        </MenuItem>
                                    ))}
                                </Menu>
                            </Dropdown>
                        ) : (
                            <IconButton size={"sm"}>
                                <EllipsisVertical size={16} />
                            </IconButton>
                        )}
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}

const DocumentStatusChip = ({ status }: { status: string }) => {
    const config = statusConfig[status] || {
        label: status,
        icon: null,
        color: "neutral",
    };

    return (
        <Chip
            sx={{
                position: "absolute",
                right: 8,
                top: 8,
                borderRadius: 6,
                border: "1px solid",
                borderColor: "divider",
            }}
            variant="soft"
            size="sm"
            color={config.color}
            startDecorator={config.icon}
        >
            {config.label}
        </Chip>
    );
};

function Line(props: any) {
    return (
        <Box
            sx={(theme: any) => {
                let bgColor = "divider";

                if (props.bgcolor) {
                    const [key, subkey] = props.bgcolor.split(".");
                    bgColor = theme.palette[key]?.[subkey] || "divider";
                }

                return {
                    height: 6,
                    width: "100%",
                    borderRadius: "lg",
                    ...props,
                    bgcolor: bgColor,
                };
            }}
        />
    );
}
